import { useMemo } from 'react'

/**
 * Procedural pixel-art avatar based on a deterministic hash of the username.
 *
 * Replay sessions do not carry Mojang skin textures, so we cannot render the
 * real player head. Instead we generate a stable 8×8 mirrored glyph that
 * looks like a stylised head — same username always produces the same face,
 * so viewers visually recognise "who is who" across clips.
 */
export default function PlayerAvatar ({ name, size = 16 }: { name: string; size?: number }) {
  const { bg, fg, accent, cells } = useMemo(() => generateFace(name), [name])
  const px = 100 / 8
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 8 8"
      shapeRendering="crispEdges"
      style={{ display: 'block', imageRendering: 'pixelated' }}
      aria-label={`avatar of ${name}`}
    >
      {/* Head base */}
      <rect x="0" y="0" width="8" height="8" fill={bg} />
      {/* Generated symmetric features */}
      {cells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width="1" height="1" fill={c.accent ? accent : fg} />
      ))}
      {/* Subtle top highlight */}
      <rect x="0" y="0" width="8" height="1" fill="rgba(255,255,255,0.08)" />
      {/* Bottom shadow */}
      <rect x="0" y="7" width="8" height="1" fill="rgba(0,0,0,0.18)" />
      <title>{name}</title>
    </svg>
  )
}

/** Cheap but stable string hash. */
function hashStr (s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = (h * 16777619) >>> 0
  }
  return h
}

function generateFace (name: string) {
  const h = hashStr(name || '?')

  // Colors: pick from curated palettes so avatars always look readable.
  const BG = ['#2a2015', '#1f2a35', '#2a1f35', '#152b1f', '#352015', '#1a2035']
  const FG = ['#d4a84b', '#7ec8e3', '#c197e8', '#7ed4a0', '#e8a07a', '#9ac5e8']
  const AC = ['#f0c066', '#a8dff5', '#d8b0f2', '#a8e8c4', '#f2c19a', '#c0dcf2']

  const bg = BG[h % BG.length]!
  const fg = FG[(h >> 3) % FG.length]!
  const accent = AC[(h >> 6) % AC.length]!

  // 4×4 mirrored pattern (like GitHub identicons) → 8×8 canvas.
  // Reserve row 0 and row 7 for aesthetic strips, use rows 1..6, cols 0..3
  // mirrored to cols 4..7.
  const cells: { x: number; y: number; accent: boolean }[] = []
  let bits = h
  for (let y = 1; y <= 6; y++) {
    for (let x = 0; x < 4; x++) {
      const b = bits & 1
      bits = bits >>> 1
      if (bits === 0) bits = hashStr(`${name}#${y}${x}`)
      if (!b) continue
      const isAccent = (y === 2 || y === 3) && (x === 1) // eye position roughly
      cells.push({ x, y, accent: isAccent })
      cells.push({ x: 7 - x, y, accent: isAccent })
    }
  }
  return { bg, fg, accent, cells }
}
